import {useEffect} from 'react';
import {useSelector} from 'react-redux';

import {getLoyaltySettings} from '@/config/config';
import storageService from '@/services/storage/storageService';
import {selectConsumerId} from '@/state/selectors/consumer';
import {selectAllExpiringJourneys} from '@/state/selectors/consumerLoyalty';
import {showExpiringBadgePopup} from '@/state/slices/modalSlice';
import {dispatch} from '@/state/store';

const SECONDS_IN_DAY = 24 * 60 * 60;

const useExpiringBadge = () => {
    const ranges = getLoyaltySettings().expirationPopupReminderDays || [];
    const journeys = useSelector(selectAllExpiringJourneys);
    const consumerId = useSelector(selectConsumerId);

    const getShownPopupData = () => {
        try {
            const data = storageService.getExpiringBadgeDataFromLocalStorage();

            return data ? JSON.parse(data) : {};
        } catch {
            return {};
        }
    };

    const saveShownPopupData = (range, journeyId) => {
        const currentData = getShownPopupData();

        currentData[journeyId] = range;
        storageService.setExpiringBadgeDataToLocalStorage(JSON.stringify(currentData));
    };

    const cleanupExpiredJourneys = (validJourneyIds) => {
        const currentData = getShownPopupData();
        const filteredData = {};

        validJourneyIds.forEach((journeyId) => {
            if (currentData[journeyId] !== undefined) {
                filteredData[journeyId] = currentData[journeyId];
            }
        });

        storageService.setExpiringBadgeDataToLocalStorage(JSON.stringify(filteredData));
    };

    const calculateDaysUntilExpiration = (expireAt) => {
        const today = new Date();
        const expirationDate = new Date(expireAt);

        today.setHours(0, 0, 0, 0);
        expirationDate.setHours(0, 0, 0, 0);

        const timeDifference = expirationDate.getTime() - today.getTime();
        const days = timeDifference / (1000 * 60 * 60 * 24);

        return Math.ceil(days);
    };

    const findRangeToShow = (daysUntilExpiration, lastShownRange, sortedRanges) => {
        for (const range of sortedRanges) {
            if (daysUntilExpiration <= range) {
                return lastShownRange === undefined || lastShownRange > range ? range : null;
            }
        }
        return null;
    };

    useEffect(() => {
        if (!journeys?.length || !ranges.length) {
            return;
        }

        const sortedRanges = [...ranges].sort((a, b) => a - b);
        const shownData = getShownPopupData();
        const validJourneyIds = [];

        for (const journey of journeys) {
            if (!journey?.journeyId) continue;

            const expireAt = journey?.journeyExpireAt || journey?.memberJourneyExpireAt;

            if (!expireAt) continue;

            const daysUntilExpiration = calculateDaysUntilExpiration(expireAt);

            // If already expired, skip
            if (daysUntilExpiration <= 0) continue;

            validJourneyIds.push(journey.journeyId);

            const lastShownRange = shownData[journey.journeyId];
            const rangeToShow = findRangeToShow(daysUntilExpiration, lastShownRange, sortedRanges);

            if (rangeToShow) {
                saveShownPopupData(rangeToShow, journey.journeyId);

                dispatch(
                    showExpiringBadgePopup({
                        journeyId: journey.journeyId,
                        timeInSeconds: daysUntilExpiration * SECONDS_IN_DAY,
                    })
                );
                break;
            }
        }

        cleanupExpiredJourneys(validJourneyIds);
    }, [journeys, consumerId, ranges]);
};

export default useExpiringBadge;
